/* eslint-disable max-lines-per-function */
import { useCallback, useEffect, useState } from 'react';

import type { DevelopmentSummaryModel } from '@/domain/models/development.js';
import type { ImportDevelopmentsResult } from '@/domain/use-cases/developments.js';
import type { DevelopmentController } from '@/presentation/controllers/development-controller.js';
import { useApp } from '@/presentation/hooks/use-app.js';
import { createSessionPreference } from '@/presentation/pages/developments/utils/session-preference.js';
import type { DevelopmentCategory } from '@/shared/utils/format-status.js';

type State = {
    developments: DevelopmentSummaryModel[];
    total: number;
    page: number;
    totalPages: number;
    isLoading: boolean;
    error: string | null;
};

const SHOW_CANCELED_KEY = 'devList:showCanceled';
const STATUS_FILTER_KEY = 'devList:statusFilter';

const showCanceledPref = createSessionPreference<boolean>(
    SHOW_CANCELED_KEY,
    (v) => !v,
    (v): v is boolean => typeof v === 'boolean'
);
const statusFilterPref = createSessionPreference<string[]>(
    STATUS_FILTER_KEY,
    (v) => v.length === 0,
    (v): v is string[] => Array.isArray(v) && v.every((item) => typeof item === 'string')
);

export const clearShowCanceledSession = showCanceledPref.clear;
export const clearStatusFilterSession = statusFilterPref.clear;

// O backend considera sucesso mesmo quando pula todas as linhas (consultor inexistente,
// vinculo inexistente). Sem traduzir isso o usuario ve o modal fechar e a lista vazia.
function formatImportOutcome(result: ImportDevelopmentsResult): string {
    const parts: string[] = [`${result.created} desenvolvimento(s) criado(s)`];
    if (result.skipped > 0) {
        parts.push(`${result.skipped} linha(s) ignorada(s)`);
    }
    const detail = result.warnings.map((w) => `Linha ${w.line}: ${w.message}`);
    return [parts.join(', '), ...detail].join('\n');
}

export function useDevelopments(controller: DevelopmentController) {
    const { useProjectStore } = useApp();
    const selectedProject = useProjectStore((s) => s.selectedProject);
    const [state, setState] = useState<State>({ developments: [], total: 0, page: 1, totalPages: 1, isLoading: true, error: null });
    const [search, setSearchState] = useState('');
    const [statusFilter, setStatusFilterState] = useState<string[]>(() => (selectedProject ? statusFilterPref.read(selectedProject.id, []) : []));
    const [showCanceled, setShowCanceledState] = useState(() => (selectedProject ? showCanceledPref.read(selectedProject.id, false) : false));
    const [page, setPage] = useState(1);
    const [limit, setLimitState] = useState(20);

    useEffect(() => {
        if (selectedProject) {
            setShowCanceledState(showCanceledPref.read(selectedProject.id, false));
            setStatusFilterState(statusFilterPref.read(selectedProject.id, []));
            setPage(1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- apenas reexecuta quando o projeto muda
    }, [selectedProject?.id]);

    const setSearch = useCallback((value: string) => {
        setSearchState(value);
        setPage(1);
    }, []);

    const setStatusFilter = useCallback(
        (value: string[]) => {
            setStatusFilterState(value);
            setPage(1);
            if (selectedProject) {
                statusFilterPref.persist(selectedProject.id, value);
            }
        },
        [selectedProject?.id]
    );

    const setShowCanceled = useCallback(
        (value: boolean) => {
            setShowCanceledState(value);
            setPage(1);
            if (selectedProject) {
                showCanceledPref.persist(selectedProject.id, value);
            }
        },
        [selectedProject?.id]
    );

    const setLimit = useCallback((value: number) => {
        setLimitState(value);
        setPage(1);
    }, []);

    const load = useCallback(async () => {
        if (!selectedProject) {
            setState({ developments: [], total: 0, page: 1, totalPages: 1, isLoading: false, error: null });
            return;
        }
        setState((s) => ({ ...s, isLoading: true, error: null }));
        const result = await controller.loadDevelopments({
            search: search || undefined,
            statuses: statusFilter.length > 0 ? statusFilter : undefined,
            projectId: selectedProject.id,
            page,
            limit,
            includeCanceled: showCanceled
        });
        result.fold(
            (error) => setState({ developments: [], total: 0, page: 1, totalPages: 1, isLoading: false, error: error.message }),
            (data) => setState({ developments: data.data, total: data.total, page: data.page, totalPages: data.totalPages, isLoading: false, error: null })
        );
    }, [controller, selectedProject, search, statusFilter, showCanceled, page, limit]);

    useEffect(() => {
        load();
    }, [load]);

    const createDevelopment = useCallback(
        async (params: {
            title: string;
            description: string;
            businessProcessId?: string;
            projectId: string;
            consultorId?: string;
            usuarioChaveId?: string;
            linkedDevelopmentId?: string;
            functionalComplexity: 'MUITO_BAIXA' | 'BAIXA' | 'MEDIA' | 'ALTA' | 'MUITO_ALTA' | null;
            functionalHours: number | null;
            techComplexity: 'MUITO_BAIXA' | 'BAIXA' | 'MEDIA' | 'ALTA' | 'MUITO_ALTA' | null;
            techHours: number | null;
            estimatedDeliveryDate: string | null;
            category: DevelopmentCategory | null;
        }) => {
            const result = await controller.createDevelopment(params);
            return result;
        },
        [controller]
    );

    const [exporting, setExporting] = useState(false);
    const [exportError, setExportError] = useState<string | null>(null);

    const exportExcel = useCallback(async () => {
        if (!selectedProject) {
            return;
        }
        setExporting(true);
        setExportError(null);
        try {
            const result = await controller.exportDevelopments({
                search: search || undefined,
                statuses: statusFilter.length > 0 ? statusFilter : undefined,
                projectId: selectedProject.id,
                includeCanceled: showCanceled
            });
            result.fold(
                (error) => setExportError(error.message),
                ({ blob, filename }) => {
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = filename;
                    link.click();
                    setTimeout(() => URL.revokeObjectURL(url), 1000);
                }
            );
        } catch (error) {
            setExportError(error instanceof Error ? error.message : 'export failed');
        } finally {
            setExporting(false);
        }
    }, [controller, selectedProject, search, statusFilter, showCanceled]);

    const [importing, setImporting] = useState(false);
    const [importError, setImportError] = useState<string | null>(null);
    const [selectedImportFile, setSelectedImportFile] = useState<File | null>(null);
    const [importSummary, setImportSummary] = useState<string | null>(null);

    const importExcel = useCallback(
        async (file: File): Promise<ImportDevelopmentsResult | null> => {
            if (!selectedProject) {
                setImportError('Selecione um projeto antes de importar');
                return null;
            }
            setImporting(true);
            setImportError(null);
            setImportSummary(null);
            try {
                const result = await controller.importDevelopments(file, selectedProject.id);
                const imported = result.fold<ImportDevelopmentsResult | null>(
                    (error) => {
                        setImportError(error.message);
                        return null;
                    },
                    (data) => data
                );
                setImporting(false);
                if (!imported) {
                    return null;
                }
                if (imported.created === 0) {
                    setImportError(formatImportOutcome(imported));
                    return null;
                }
                setSelectedImportFile(null);
                setImportSummary(formatImportOutcome(imported));
                await load();
                return imported;
            } catch (err) {
                setImporting(false);
                setImportError(err instanceof Error ? err.message : 'Erro inesperado ao importar');
                return null;
            }
        },
        [controller, selectedProject, load]
    );

    const clearImportError = useCallback(() => {
        setImportError(null);
        setImportSummary(null);
    }, []);

    return {
        ...state,
        selectedProject,
        search,
        setSearch,
        statusFilter,
        setStatusFilter,
        showCanceled,
        setShowCanceled,
        page,
        setPage,
        limit,
        setLimit,
        reload: load,
        createDevelopment,
        exportExcel,
        exporting,
        exportError,
        importExcel,
        importing,
        importError,
        importSummary,
        selectedImportFile,
        setSelectedImportFile,
        clearImportError
    };
}
